import { Toast as BaseToast } from "@base-ui/react/toast";
import { cn } from "./cn";

/**
 * Toasts (Base UI). A single global manager so any module can fire a toast
 * without prop-drilling:
 *
 *   import { toast } from "@/ui";
 *   toast.success("Sent to 1 recipient.");
 *   toast.error("Subject is required.");
 *
 * Mount <ToastProvider> once near the app root (see main.tsx).
 */
export const toastManager = BaseToast.createToastManager();

export const toast = {
    success: (title: string, description?: string) =>
        toastManager.add({ title, description, type: "success" }),
    error: (title: string, description?: string) =>
        toastManager.add({ title, description, type: "error", timeout: 8000 }),
    info: (title: string, description?: string) =>
        toastManager.add({ title, description, type: "info" }),
};

const ACCENT: Record<string, string> = {
    success: "border-l-4 border-l-green-600",
    error:   "border-l-4 border-l-red-600",
    info:    "border-l-4 border-l-blue-600",
};

function ToastList() {
    const { toasts } = BaseToast.useToastManager();
    return toasts.map((t) => (
        <BaseToast.Root
            key={t.id}
            toast={t}
            className={cn(
                "pointer-events-auto w-80 max-w-[calc(100vw-2rem)] rounded-md border border-border bg-surface p-3 shadow-lg",
                "transition-all duration-200",
                "data-[starting-style]:translate-x-4 data-[starting-style]:opacity-0",
                "data-[ending-style]:translate-x-4 data-[ending-style]:opacity-0",
                ACCENT[t.type ?? ""] ?? "",
            )}
        >
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <BaseToast.Title className="text-sm font-semibold text-fg" />
                    <BaseToast.Description className="mt-0.5 text-sm text-muted" />
                </div>
                <BaseToast.Close
                    aria-label="Dismiss"
                    className="-mr-1 -mt-1 shrink-0 rounded p-1 text-muted hover:text-fg"
                >
                    ✕
                </BaseToast.Close>
            </div>
        </BaseToast.Root>
    ));
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
    return (
        <BaseToast.Provider toastManager={toastManager}>
            {children}
            <BaseToast.Portal>
                <BaseToast.Viewport className="pointer-events-none fixed bottom-4 right-4 z-[2000] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
                    <ToastList />
                </BaseToast.Viewport>
            </BaseToast.Portal>
        </BaseToast.Provider>
    );
}
